import { useMemo } from 'react';

import {
	ChartSectionData,
	Generation,
	SubmissionRecord,
	TopTenEntry,
	birthYearToGeneration,
	deriveTopTen,
	getDateBounds,
	makeSection,
} from '@/helpers/reportsDataHelpers';
import { useAuth } from '@/providers/AuthProvider';
import { useServices } from '@/providers/ServicesProvider/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';


import { ReportsDateRange, ReportsScope } from './useReportsAccess';

function toLocalDateKey(isoString: string): string {
	const d = new Date(isoString);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────
// Public parameter interface
// ─────────────────────────────────────────────────────────────

export interface ReportsDataParams {
	scope: ReportsScope;
	dateRange: ReportsDateRange;
	/**
	 * The calendar year to use when dateRange === 'year'.
	 * Omit (or pass undefined) to default to the current year.
	 */
	selectedYear?: number;
	/**
	 * Narrows radar/distribution charts to submissions from users with these
	 * skill values. Empty array = no filter (show all).
	 */
	skillFilter: string[];
	/**
	 * Narrows radar/distribution charts to submissions from users with these
	 * gender values (e.g. 'male', 'female'). Empty array = no filter (show all).
	 * NOTE: gender is not currently fetched in the GetMyTeamQuery; radarByGender
	 * will always return empty data until the TeamProvider GQL is extended.
	 */
	genderFilter: string[];
	/**
	 * Narrows radar/distribution charts to submissions from users whose birth
	 * year maps to one of these Generation labels (e.g. 'Gen Z', 'Millennial').
	 * Empty array = no filter (show all).
	 */
	generationFilter: string[];
	/**
	 * Narrows all charts to setlists belonging to these service IDs.
	 * Empty array = no filter (show all).
	 *
	 * IMPORTANT: Pass stable array references from useState/useCallback to avoid
	 * cascade re-renders. useMemo uses Object.is comparison — a new [] on every
	 * render triggers unnecessary re-derivation.
	 */
	serviceFilter: string[];
	/**
	 * Optional override setlists data.
	 * Used by the Reports page to load a richer query without relying on the
	 * global SetlistsProvider (which may intentionally omit heavy fields).
	 */
	setlistsOverride?: ReportsSetlist[];
}

// Minimal structural type of the fields this hook reads from `setlists`.
// We keep it local so it can be satisfied by multiple GraphQL queries.
export type ReportsSetlist = {
	id: string;
	startAt: string;
	serviceId?: string | null;
	draft?: boolean | null;
	teamSongIds: string[];
	feedback?: {
		submissions?: Array<{
			userId: string;
			teamSongFeedbackSubmissions?: Array<{
				teamSongId: string;
				personalScore: number;
				teamScore: number;
				congregationalScore: number;
			}> | null;
		}> | null;
	} | null;
};

// ─────────────────────────────────────────────────────────────
// Data point interfaces
// ─────────────────────────────────────────────────────────────

export interface SongsPerformedDataPoint {
	date: string;   // ISO date string YYYY-MM-DD
	count: number;
}

export interface TagDistributionPoint {
	label: string;  // tag value, e.g. 'joy', 'glory of god'
	count: number;
}

export interface TagTrendPoint {
	setlistId: string;  // unique setlist identifier — used as X-axis slot
	date: string;       // ISO date string YYYY-MM-DD — used for axis display and sorting
	label: string;      // tag value
	count: number;
}

export interface RadarDataPoint {
	segment: string;           // skill name / generation label / service name
	personalScore: number;     // average across all submissions in segment
	teamScore: number;
	congregationalScore: number;
	submissionCount: number;
}

export interface SentimentHistoryPoint {
	setlistId: string;
	date: string;      // ISO date string YYYY-MM-DD
	personalScore: number;
	teamScore: number;
	congregationalScore: number;
}

export interface SentimentQuartiles {
	q1: number;
	q2: number;
	q3: number;
}

export interface AverageSentimentData {
	personalScoreQuartiles: SentimentQuartiles;
	teamScoreQuartiles: SentimentQuartiles;
	congregationalScoreQuartiles: SentimentQuartiles;
	myPersonalScore: number | null;
	myTeamScore: number | null;
	myCongregationalScore: number | null;
}

// ─────────────────────────────────────────────────────────────
// Hook return interface
// ─────────────────────────────────────────────────────────────

export interface ReportsData {
	songsPerformed: ChartSectionData<SongsPerformedDataPoint>;
	themeDistribution: ChartSectionData<TagDistributionPoint>;
	theologyDistribution: ChartSectionData<TagDistributionPoint>;
	emotionDistribution: ChartSectionData<TagDistributionPoint>;
	gospelStoryDistribution: ChartSectionData<TagDistributionPoint>;
	themeTrend: ChartSectionData<TagTrendPoint>;
	theologyTrend: ChartSectionData<TagTrendPoint>;
	emotionTrend: ChartSectionData<TagTrendPoint>;
	gospelStoryTrend: ChartSectionData<TagTrendPoint>;
	radarByGender: ChartSectionData<RadarDataPoint>;
	radarByGeneration: ChartSectionData<RadarDataPoint>;
	radarByServiceType: ChartSectionData<RadarDataPoint>;
	radarBySkill: ChartSectionData<RadarDataPoint>;
	sentimentHistory: ChartSectionData<SentimentHistoryPoint>;
	averageSentiment: ChartSectionData<AverageSentimentData>;
	topTen: ChartSectionData<TopTenEntry>;
}


// ─────────────────────────────────────────────────────────────
// useReportsData
// ─────────────────────────────────────────────────────────────

/**
 * Derives all chart data from existing providers given the active filter state.
 *
 * Architecture: layered useMemos
 *  1. songMap and memberMap — O(1) lookup structures
 *  2. relevantSetlists — shared base: date + service + draft-excluded
 *  3. allNonDraftSetlists — for emptyReason computation only
 *  4. filteredSubmissions / unfilteredSubmissions — scope + demographic filters
 *  5. Per-chart derivations — fan out from the above
 *
 * All derivations are pure transformations of already-loaded provider data.
 * No network calls. No side effects.
 */
export function useReportsData(params: ReportsDataParams): ReportsData {
	// ── Provider data ──────────────────────────────────────────
	const { setlists: setlistsFromProvider } = useSetlists();
	const setlists = (params.setlistsOverride ?? setlistsFromProvider) as ReportsSetlist[];
	const { teamSongs } = useTeamSongs();
	const { team, findTeamMembersByUserId } = useTeam();
	const { user } = useAuth();
	const { services } = useServices();

	// ── Lookup maps ────────────────────────────────────────────

	/** O(1) teamSongId → song lookup for tag derivation. */
	const songMap = useMemo(
		() => new Map(teamSongs.map((s) => [s.id, s])),
		[teamSongs],
	);

	/**
	 * O(1) userId → team member user lookup for demographic/skill join.
	 * Note: the GQL query for team members includes skills and birthYear,
	 * but NOT gender — see GetMyTeamQuery. The `gender` field is on the User
	 * GraphQL type but is not requested by TeamProvider's GQL document.
	 */
	const memberMap = useMemo(
		() => new Map(team.members.map((m) => [m.user.id, m.user])),
		[team.members],
	);

	/** O(1) serviceId → service lookup for radarByServiceType derivation. */
	const serviceMap = useMemo(
		() => new Map(services.map((s) => [s.id, s])),
		[services],
	);

	// ── Shared base: relevant setlists ─────────────────────────

	/**
	 * Setlists that pass:
	 *   1. Draft exclusion (draft === true setlists are never real performances)
	 *   2. Date range filter (based on setlist.startAt, the performance date)
	 *   3. Service type filter (empty serviceFilter = no filter applied)
	 */
	const relevantSetlists = useMemo(() => {
		const { from, to } = getDateBounds(params.dateRange, params.selectedYear);
		return setlists.filter((s) => {
			if (s.draft) return false;
			const date = new Date(s.startAt);
			if (date < from || date > to) return false;
			if (params.serviceFilter.length > 0 && s.serviceId) {
				if (!params.serviceFilter.includes(s.serviceId)) return false;
			}
			return true;
		});
	}, [setlists, params.dateRange, params.selectedYear, params.serviceFilter]);

	/**
	 * All non-draft setlists, no date or service filter applied.
	 * Used only to determine emptyReason: 'no-history' vs 'date-filtered'.
	 */
	const allNonDraftSetlists = useMemo(
		() => setlists.filter((s) => !s.draft),
		[setlists],
	);

	// ── Scope-filtered submission collector ────────────────────

	/**
	 * Collect SubmissionRecord items from `relevantSetlists` (date-filtered,
	 * service-filtered) based on scope and demographic/skill filters.
	 *
	 * - Personal scope: only the current user's own feedback submissions
	 * - Team scope: all feedback submissions from any user
	 *
	 * Skill / generation filters narrow which submitters to include.
	 * Empty filter arrays = no filter (show all).
	 */
	const filteredSubmissions = useMemo<SubmissionRecord[]>(() => {
		const result: SubmissionRecord[] = [];

		for (const setlist of relevantSetlists) {
			if (!setlist.feedback?.submissions) continue;

			const candidates =
				params.scope === 'personal'
					? // Personal: only include this user's submission (guard: skip if no user.id)
					  user?.id
						? setlist.feedback.submissions.filter(
								(sub) => sub.userId === user.id,
							)
						: []
					: setlist.feedback.submissions;

			for (const submission of candidates) {
				const userId = submission.userId;
				const member = memberMap.get(userId);

				// ── Skill filter ──────────────────────────────────────
				if (params.skillFilter.length > 0) {
					const userSkills: string[] = member?.skills ?? [];
					const hasMatchingSkill = params.skillFilter.some((s) =>
						userSkills.includes(s),
					);
					if (!hasMatchingSkill) continue;
				}

				// ── Generation filter ─────────────────────────────────
				if (params.generationFilter.length > 0) {
					const generation: Generation = birthYearToGeneration(member?.birthYear);
					if (!params.generationFilter.includes(generation)) continue;
				}

				// ── Gender filter ─────────────────────────────────────
				if (params.genderFilter.length > 0) {
					if (!member?.gender || !params.genderFilter.includes(member.gender)) continue;
				}

				for (const tsf of submission.teamSongFeedbackSubmissions ?? []) {
					result.push({
						setlistId: setlist.id,
						setlistStartAt: setlist.startAt,
						serviceId: setlist.serviceId,
						teamSongId: tsf.teamSongId,
						personalScore: tsf.personalScore,
						teamScore: tsf.teamScore,
						congregationalScore: tsf.congregationalScore,
						userId,
					});
				}
			}
		}

		return result;
	}, [
		relevantSetlists,
		params.scope,
		params.skillFilter,
		params.genderFilter,
		params.generationFilter,
		user?.id,
		memberMap,
	]);

	/**
	 * The current user's own submissions from relevantSetlists (date + service
	 * filtered only — no demographic or skill filters applied). Used to anchor
	 * the user avatar on the Average Sentiment card regardless of active filters.
	 */
	const myRelevantSubmissions = useMemo<SubmissionRecord[]>(() => {
		if (!user?.id) return [];
		const result: SubmissionRecord[] = [];
		for (const setlist of relevantSetlists) {
			if (!setlist.feedback?.submissions) continue;
			const mySub = setlist.feedback.submissions.find((sub) => sub.userId === user.id);
			if (!mySub) continue;
			for (const tsf of mySub.teamSongFeedbackSubmissions ?? []) {
				result.push({
					setlistId: setlist.id,
					setlistStartAt: setlist.startAt,
					serviceId: setlist.serviceId,
					teamSongId: tsf.teamSongId,
					personalScore: tsf.personalScore,
					teamScore: tsf.teamScore,
					congregationalScore: tsf.congregationalScore,
					userId: user.id,
				});
			}
		}
		return result;
	}, [relevantSetlists, user?.id]);

	/**
	 * Unfiltered submissions: all non-draft setlists, team scope, no chips.
	 * Used to compute emptyReason across all chart sections.
	 */
	const unfilteredSubmissions = useMemo<SubmissionRecord[]>(() => {
		const result: SubmissionRecord[] = [];
		for (const setlist of allNonDraftSetlists) {
			if (!setlist.feedback?.submissions) continue;
			for (const submission of setlist.feedback.submissions) {
				for (const tsf of submission.teamSongFeedbackSubmissions ?? []) {
					result.push({
						setlistId: setlist.id,
						setlistStartAt: setlist.startAt,
						serviceId: setlist.serviceId,
						teamSongId: tsf.teamSongId,
						personalScore: tsf.personalScore,
						teamScore: tsf.teamScore,
						congregationalScore: tsf.congregationalScore,
						userId: submission.userId,
					});
				}
			}
		}
		return result;
	}, [allNonDraftSetlists]);

	// ── TOP-10: Top songs by composite score ──────────────────

	const topTen = useMemo<ChartSectionData<TopTenEntry>>(() => {
		const filtered = deriveTopTen(filteredSubmissions, songMap);
		const unfiltered = deriveTopTen(unfilteredSubmissions, songMap);
		return makeSection(filtered, unfiltered);
	}, [filteredSubmissions, unfilteredSubmissions, songMap]);

	// ── SONG-01: Songs performed by date ───────────────────────

	const songsPerformed = useMemo<ChartSectionData<SongsPerformedDataPoint>>(() => {
		// Group relevant setlists by ISO date, count songs played per date.
		// This is setlist-level (all songs in the setlist), not submission-level.
		const byDate: Record<string, number> = {};
		for (const setlist of relevantSetlists) {
			const dateKey = toLocalDateKey(setlist.startAt);
			byDate[dateKey] = (byDate[dateKey] ?? 0) + setlist.teamSongIds.length;
		}

		const filtered: SongsPerformedDataPoint[] = Object.entries(byDate)
			.map(([date, count]) => ({ date, count }))
			.sort((a, b) => a.date.localeCompare(b.date));

		const unfiltered: SongsPerformedDataPoint[] = (() => {
			const allByDate: Record<string, number> = {};
			for (const setlist of allNonDraftSetlists) {
				const dateKey = toLocalDateKey(setlist.startAt);
				allByDate[dateKey] = (allByDate[dateKey] ?? 0) + setlist.teamSongIds.length;
			}
			return Object.entries(allByDate).map(([date, count]) => ({ date, count }));
		})();

		return makeSection(filtered, unfiltered);
	}, [relevantSetlists, allNonDraftSetlists]);

	// ── Tag accumulator helper ─────────────────────────────────

	/** Accumulate tag counts from an array of submissions using a tag getter fn. */
	function accumulateTags(
		submissions: SubmissionRecord[],
		getTags: (song: NonNullable<ReturnType<typeof songMap['get']>>) => string[],
	): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			if (!song) continue;
			for (const tag of getTags(song)) {
				acc[tag] = (acc[tag] ?? 0) + 1;
			}
		}
		return acc;
	}

	/** Convert an accumulator object to a sorted TagDistributionPoint array (desc by count). */
	function toDistributionPoints(acc: Record<string, number>): TagDistributionPoint[] {
		return Object.entries(acc)
			.map(([label, count]) => ({ label, count }))
			.sort((a, b) => b.count - a.count);
	}

	// ── DIST-01: Tag distribution charts ──────────────────────

	const themeDistribution = useMemo<ChartSectionData<TagDistributionPoint>>(() => {
		// Reads analysisOutput.thematicKeywords (strength-weighted) from setlists directly.
		const filtered = toDistributionPoints(accumulateKeywordsFromSetlists(relevantSetlists, 'thematic'));
		const unfiltered = toDistributionPoints(accumulateKeywordsFromSetlists(allNonDraftSetlists, 'thematic'));
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const theologyDistribution = useMemo<ChartSectionData<TagDistributionPoint>>(() => {
		// Reads analysisOutput.theologicalKeywords (strength-weighted) from setlists directly.
		const filtered = toDistributionPoints(accumulateKeywordsFromSetlists(relevantSetlists, 'theological'));
		const unfiltered = toDistributionPoints(accumulateKeywordsFromSetlists(allNonDraftSetlists, 'theological'));
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const gospelStoryDistribution = useMemo<ChartSectionData<TagDistributionPoint>>(() => {
		// Uses gospelTags flat array from setlists directly. Will migrate to analysisOutput.gospelStory once
		// that field is added to the FullAnalysisOutput GQL schema.
		const filtered = toDistributionPoints(
			accumulateTagsFromSetlists(relevantSetlists, (s) => s.gospelTags),
		);
		const unfiltered = toDistributionPoints(
			accumulateTagsFromSetlists(allNonDraftSetlists, (s) => s.gospelTags),
		);
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const emotionDistribution = useMemo<ChartSectionData<TagDistributionPoint>>(() => {
		// Reads analysisOutput.emotions (strength-weighted) from setlists directly.
		const filtered = toDistributionPoints(accumulateEmotionsFromSetlists(relevantSetlists));
		const unfiltered = toDistributionPoints(accumulateEmotionsFromSetlists(allNonDraftSetlists));
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	// ── DIST-02: Trend charts ─────────────────────────────────

	/** Accumulate trend points keyed by {date}:{label}. */
	function accumulateTrend(
		submissions: SubmissionRecord[],
		getTags: (song: NonNullable<ReturnType<typeof songMap['get']>>) => string[],
	): TagTrendPoint[] {
		// Key by setlistId so each service occasion is a separate X-axis slot
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			if (!song) continue;
			const date = record.setlistStartAt.slice(0, 10);
			for (const tag of getTags(song)) {
				const key = `${record.setlistId}::${tag}`;
				const existing = acc[key];
				acc[key] = {
					date,
					setlistId: record.setlistId,
					count: (existing?.count ?? 0) + 1,
				};
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	/**
	 * Accumulate keyword strengths from analysisOutput.thematicKeywords or
	 * analysisOutput.theologicalKeywords, depending on `kind`.
	 * Returns a Record keyed by the keyword label, value is summed strength.
	 */
	function accumulateKeywords(
		submissions: SubmissionRecord[],
		kind: 'thematic' | 'theological',
	): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			const items =
				kind === 'thematic'
					? song?.analysisOutput?.thematicKeywords ?? []
					: song?.analysisOutput?.theologicalKeywords ?? [];
			for (const item of items) {
				const label = 'theme' in item ? item.theme : (item as { theology: string }).theology;
				const strength = item.strength;
				if (label && strength != null) {
					acc[label] = (acc[label] ?? 0) + strength;
				}
			}
		}
		return acc;
	}

	/**
	 * Accumulate keyword trend points keyed by {date}::{label}, summing strengths
	 * from analysisOutput.thematicKeywords or analysisOutput.theologicalKeywords.
	 */
	function accumulateKeywordTrend(
		submissions: SubmissionRecord[],
		kind: 'thematic' | 'theological',
	): TagTrendPoint[] {
		// Key by setlistId so each service occasion is a separate X-axis slot
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			const items =
				kind === 'thematic'
					? song?.analysisOutput?.thematicKeywords ?? []
					: song?.analysisOutput?.theologicalKeywords ?? [];
			const date = record.setlistStartAt.slice(0, 10);
			for (const item of items) {
				const label = 'theme' in item ? item.theme : (item as { theology: string }).theology;
				const strength = item.strength;
				if (label && strength != null) {
					const key = `${record.setlistId}::${label}`;
					const existing = acc[key];
					acc[key] = {
						date,
						setlistId: record.setlistId,
						count: (existing?.count ?? 0) + strength,
					};
				}
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	/**
	 * Accumulate emotion strengths from analysisOutput.emotions.
	 * Returns a Record keyed by emotion label, value is summed strength across
	 * all submissions. Only emissions present in at least one song are included —
	 * so the array is empty when no songs have analysisOutput data.
	 */
	function accumulateEmotions(submissions: SubmissionRecord[]): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			const emotions = song?.analysisOutput?.emotions ?? [];
			for (const item of emotions) {
				if (item.emotion && item.strength != null) {
					acc[item.emotion] = (acc[item.emotion] ?? 0) + item.strength;
				}
			}
		}
		return acc;
	}

	/**
	 * Accumulate emotion trend points keyed by {date}::{emotion}, summing
	 * strengths from analysisOutput.emotions across all submissions.
	 */
	function accumulateEmotionTrend(submissions: SubmissionRecord[]): TagTrendPoint[] {
		// Key by setlistId so each service occasion is a separate X-axis slot
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const record of submissions) {
			const song = songMap.get(record.teamSongId);
			const emotions = song?.analysisOutput?.emotions ?? [];
			const date = record.setlistStartAt.slice(0, 10);
			for (const item of emotions) {
				if (item.emotion && item.strength != null) {
					const key = `${record.setlistId}::${item.emotion}`;
					const existing = acc[key];
					acc[key] = {
						date,
						setlistId: record.setlistId,
						count: (existing?.count ?? 0) + item.strength,
					};
				}
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	/** Accumulate keyword strengths from setlists directly (no submissions required). */
	function accumulateKeywordsFromSetlists(
		setlists: ReportsSetlist[],
		kind: 'thematic' | 'theological',
	): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const setlist of setlists) {
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				const items =
					kind === 'thematic'
						? song?.analysisOutput?.thematicKeywords ?? []
						: song?.analysisOutput?.theologicalKeywords ?? [];
				for (const item of items) {
					const label = 'theme' in item ? item.theme : (item as { theology: string }).theology;
					const strength = item.strength;
					if (label && strength != null) {
						acc[label] = (acc[label] ?? 0) + strength;
					}
				}
			}
		}
		return acc;
	}

	/** Accumulate keyword trend points from setlists directly. */
	function accumulateKeywordTrendFromSetlists(
		setlists: ReportsSetlist[],
		kind: 'thematic' | 'theological',
	): TagTrendPoint[] {
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const setlist of setlists) {
			const date = toLocalDateKey(setlist.startAt);
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				const items =
					kind === 'thematic'
						? song?.analysisOutput?.thematicKeywords ?? []
						: song?.analysisOutput?.theologicalKeywords ?? [];
				for (const item of items) {
					const label = 'theme' in item ? item.theme : (item as { theology: string }).theology;
					const strength = item.strength;
					if (label && strength != null) {
						const key = `${setlist.id}::${label}`;
						const existing = acc[key];
						acc[key] = {
							date,
							setlistId: setlist.id,
							count: (existing?.count ?? 0) + strength,
						};
					}
				}
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	/** Accumulate tag counts (e.g. gospelTags) from setlists directly. */
	function accumulateTagsFromSetlists(
		setlists: ReportsSetlist[],
		getTags: (song: NonNullable<ReturnType<typeof songMap['get']>>) => string[],
	): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const setlist of setlists) {
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				if (!song) continue;
				for (const tag of getTags(song)) {
					acc[tag] = (acc[tag] ?? 0) + 1;
				}
			}
		}
		return acc;
	}

	/** Accumulate tag trend points (e.g. gospelTags) from setlists directly. */
	function accumulateTagTrendFromSetlists(
		setlists: ReportsSetlist[],
		getTags: (song: NonNullable<ReturnType<typeof songMap['get']>>) => string[],
	): TagTrendPoint[] {
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const setlist of setlists) {
			const date = toLocalDateKey(setlist.startAt);
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				if (!song) continue;
				for (const tag of getTags(song)) {
					const key = `${setlist.id}::${tag}`;
					const existing = acc[key];
					acc[key] = {
						date,
						setlistId: setlist.id,
						count: (existing?.count ?? 0) + 1,
					};
				}
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	/** Accumulate emotion strengths from setlists directly. */
	function accumulateEmotionsFromSetlists(setlists: ReportsSetlist[]): Record<string, number> {
		const acc: Record<string, number> = {};
		for (const setlist of setlists) {
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				const emotions = song?.analysisOutput?.emotions ?? [];
				for (const item of emotions) {
					if (item.emotion && item.strength != null) {
						acc[item.emotion] = (acc[item.emotion] ?? 0) + item.strength;
					}
				}
			}
		}
		return acc;
	}

	/** Accumulate emotion trend points from setlists directly. */
	function accumulateEmotionTrendFromSetlists(setlists: ReportsSetlist[]): TagTrendPoint[] {
		const acc: Record<string, { date: string; setlistId: string; count: number }> = {};
		for (const setlist of setlists) {
			const date = toLocalDateKey(setlist.startAt);
			for (const songId of setlist.teamSongIds) {
				const song = songMap.get(songId);
				const emotions = song?.analysisOutput?.emotions ?? [];
				for (const item of emotions) {
					if (item.emotion && item.strength != null) {
						const key = `${setlist.id}::${item.emotion}`;
						const existing = acc[key];
						acc[key] = {
							date,
							setlistId: setlist.id,
							count: (existing?.count ?? 0) + item.strength,
						};
					}
				}
			}
		}
		return Object.entries(acc)
			.map(([key, val]) => {
				const sepIdx = key.indexOf('::');
				return { setlistId: val.setlistId, date: val.date, label: key.slice(sepIdx + 2), count: val.count };
			})
			.sort((a, b) => {
				const dateComp = a.date.localeCompare(b.date);
				return dateComp !== 0 ? dateComp : a.label.localeCompare(b.label);
			});
	}

	const themeTrend = useMemo<ChartSectionData<TagTrendPoint>>(() => {
		// Reads analysisOutput.thematicKeywords (strength-weighted) from setlists directly.
		const filtered = accumulateKeywordTrendFromSetlists(relevantSetlists, 'thematic');
		const unfiltered = accumulateKeywordTrendFromSetlists(allNonDraftSetlists, 'thematic');
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const theologyTrend = useMemo<ChartSectionData<TagTrendPoint>>(() => {
		// Reads analysisOutput.theologicalKeywords (strength-weighted) from setlists directly.
		const filtered = accumulateKeywordTrendFromSetlists(relevantSetlists, 'theological');
		const unfiltered = accumulateKeywordTrendFromSetlists(allNonDraftSetlists, 'theological');
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const gospelStoryTrend = useMemo<ChartSectionData<TagTrendPoint>>(() => {
		// Uses gospelTags flat array from setlists directly. Will migrate to analysisOutput.gospelStory once
		// that field is added to the FullAnalysisOutput GQL schema.
		const filtered = accumulateTagTrendFromSetlists(relevantSetlists, (s) => s.gospelTags);
		const unfiltered = accumulateTagTrendFromSetlists(allNonDraftSetlists, (s) => s.gospelTags);
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	const emotionTrend = useMemo<ChartSectionData<TagTrendPoint>>(() => {
		// Reads analysisOutput.emotions (strength-weighted) from setlists directly.
		const filtered = accumulateEmotionTrendFromSetlists(relevantSetlists);
		const unfiltered = accumulateEmotionTrendFromSetlists(allNonDraftSetlists);
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relevantSetlists, allNonDraftSetlists, songMap]);

	// ── Radar score accumulator helper ─────────────────────────

	interface RadarAccumulator {
		personalSum: number;
		teamSum: number;
		congregationalSum: number;
		count: number;
	}

	/** Convert a RadarAccumulator map to sorted RadarDataPoint[]. */
	function radarAccToPoints(acc: Map<string, RadarAccumulator>): RadarDataPoint[] {
		const result: RadarDataPoint[] = [];
		acc.forEach((sums, segment) => {
			if (sums.count === 0) return;
			result.push({
				segment,
				personalScore: sums.personalSum / sums.count,
				teamScore: sums.teamSum / sums.count,
				congregationalScore: sums.congregationalSum / sums.count,
				submissionCount: sums.count,
			});
		});
		return result.sort((a, b) => a.segment.localeCompare(b.segment));
	}

	/** Add a record's scores to a segment in a RadarAccumulator map. */
	function radarAccAdd(
		acc: Map<string, RadarAccumulator>,
		segment: string,
		record: SubmissionRecord,
	): void {
		const existing = acc.get(segment) ?? {
			personalSum: 0,
			teamSum: 0,
			congregationalSum: 0,
			count: 0,
		};
		acc.set(segment, {
			personalSum: existing.personalSum + record.personalScore,
			teamSum: existing.teamSum + record.teamScore,
			congregationalSum: existing.congregationalSum + record.congregationalScore,
			count: existing.count + 1,
		});
	}

	// ── RADAR-01 radarByGender ─────────────────────────────────

	/** radarByGender: group by submitter's gender value. */
	const radarByGender = useMemo<ChartSectionData<RadarDataPoint>>(() => {
		const filteredAcc = new Map<string, RadarAccumulator>();
		const unfilteredAcc = new Map<string, RadarAccumulator>();

		for (const record of filteredSubmissions) {
			const member = memberMap.get(record.userId);
			if (!member?.gender) continue;
			radarAccAdd(filteredAcc, member.gender, record);
		}

		for (const record of unfilteredSubmissions) {
			const member = findTeamMembersByUserId(record.userId);
			if (!member?.gender) continue;
			radarAccAdd(unfilteredAcc, member.gender, record);
		}

		const filtered = radarAccToPoints(filteredAcc).filter((p) => p.submissionCount > 0);
		const unfiltered = radarAccToPoints(unfilteredAcc).filter((p) => p.submissionCount > 0);

		return makeSection(filtered, unfiltered);
	}, [filteredSubmissions, unfilteredSubmissions, memberMap, findTeamMembersByUserId]);

	// ── RADAR-02 radarByGeneration ─────────────────────────────

	const radarByGeneration = useMemo<ChartSectionData<RadarDataPoint>>(() => {
		const filteredAcc = new Map<string, RadarAccumulator>();
		const unfilteredAcc = new Map<string, RadarAccumulator>();

		for (const record of filteredSubmissions) {
			const member = memberMap.get(record.userId);
			const gen = birthYearToGeneration(member?.birthYear);
			if (gen === 'Unknown') continue;
			radarAccAdd(filteredAcc, gen, record);
		}
		for (const record of unfilteredSubmissions) {
			const member = memberMap.get(record.userId);
			const gen = birthYearToGeneration(member?.birthYear);
			if (gen === 'Unknown') continue;
			radarAccAdd(unfilteredAcc, gen, record);
		}
		return makeSection(radarAccToPoints(filteredAcc), radarAccToPoints(unfilteredAcc));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredSubmissions, unfilteredSubmissions, memberMap]);

	// ── RADAR-03 radarByServiceType ────────────────────────────

	const radarByServiceType = useMemo<ChartSectionData<RadarDataPoint>>(() => {
		const filteredAcc = new Map<string, RadarAccumulator>();
		const unfilteredAcc = new Map<string, RadarAccumulator>();

		for (const record of filteredSubmissions) {
			const service = serviceMap.get(record.serviceId ?? '');
			if (!service) continue;
			radarAccAdd(filteredAcc, service.name, record);
		}
		for (const record of unfilteredSubmissions) {
			const service = serviceMap.get(record.serviceId ?? '');
			if (!service) continue;
			radarAccAdd(unfilteredAcc, service.name, record);
		}
		return makeSection(radarAccToPoints(filteredAcc), radarAccToPoints(unfilteredAcc));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredSubmissions, unfilteredSubmissions, serviceMap]);

	// ── RADAR-04 radarBySkill ──────────────────────────────────

	/** radarBySkill: group by submitter's skill values. Members with multiple skills contribute to each segment. */
	const radarBySkill = useMemo<ChartSectionData<RadarDataPoint>>(() => {
		const filteredAcc = new Map<string, RadarAccumulator>();
		const unfilteredAcc = new Map<string, RadarAccumulator>();

		for (const record of filteredSubmissions) {
			const member = memberMap.get(record.userId);
			for (const skill of member?.skills ?? []) {
				radarAccAdd(filteredAcc, skill, record);
			}
		}
		for (const record of unfilteredSubmissions) {
			const member = memberMap.get(record.userId);
			for (const skill of member?.skills ?? []) {
				radarAccAdd(unfilteredAcc, skill, record);
			}
		}
		return makeSection(radarAccToPoints(filteredAcc), radarAccToPoints(unfilteredAcc));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredSubmissions, unfilteredSubmissions, memberMap]);

	// ── Sentiment helpers ──────────────────────────────────────

	function buildSentimentHistory(submissions: SubmissionRecord[]): SentimentHistoryPoint[] {
		const bySetlist = new Map<string, {
			date: string;
			personalSum: number;
			teamSum: number;
			congregationalSum: number;
			count: number;
		}>();
		for (const record of submissions) {
			const existing = bySetlist.get(record.setlistId) ?? {
				date: record.setlistStartAt.slice(0, 10),
				personalSum: 0,
				teamSum: 0,
				congregationalSum: 0,
				count: 0,
			};
			bySetlist.set(record.setlistId, {
				date: existing.date,
				personalSum: existing.personalSum + record.personalScore,
				teamSum: existing.teamSum + record.teamScore,
				congregationalSum: existing.congregationalSum + record.congregationalScore,
				count: existing.count + 1,
			});
		}
		return [...bySetlist.entries()]
			.map(([setlistId, v]) => ({
				setlistId,
				date: v.date,
				personalScore: v.personalSum / v.count,
				teamScore: v.teamSum / v.count,
				congregationalScore: v.congregationalSum / v.count,
			}))
			.sort((a, b) => a.date.localeCompare(b.date));
	}

	function computeQuartiles(values: number[]): SentimentQuartiles {
		if (values.length === 0) return { q1: 0, q2: 0, q3: 0 };
		const sorted = [...values].sort((a, b) => a - b);
		const n = sorted.length;
		const interpolate = (p: number): number => {
			const pos = p * (n - 1);
			const lo = Math.floor(pos);
			const hi = Math.ceil(pos);
			if (lo === hi) return sorted[lo];
			return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
		};
		return {
			q1: interpolate(0.25),
			q2: interpolate(0.5),
			q3: interpolate(0.75),
		};
	}

	function buildAverageSentiment(submissions: SubmissionRecord[], mySubmissions: SubmissionRecord[]): AverageSentimentData[] {
		if (submissions.length === 0) return [];
		const mean = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / vals.length;
		const myAvg = (vals: number[]) => vals.length > 0 ? mean(vals) : null;
		const personalVals = submissions.map((r) => r.personalScore);
		const teamVals = submissions.map((r) => r.teamScore);
		const congVals = submissions.map((r) => r.congregationalScore);
		return [{
			personalScoreQuartiles: { ...computeQuartiles(personalVals), q2: mean(personalVals) },
			teamScoreQuartiles: { ...computeQuartiles(teamVals), q2: mean(teamVals) },
			congregationalScoreQuartiles: { ...computeQuartiles(congVals), q2: mean(congVals) },
			myPersonalScore: myAvg(mySubmissions.map((r) => r.personalScore)),
			myTeamScore: myAvg(mySubmissions.map((r) => r.teamScore)),
			myCongregationalScore: myAvg(mySubmissions.map((r) => r.congregationalScore)),
		}];
	}

	// ── FEEDBACK: Sentiment history ────────────────────────────

	const sentimentHistory = useMemo<ChartSectionData<SentimentHistoryPoint>>(() => {
		const filtered = buildSentimentHistory(filteredSubmissions);
		const unfiltered = buildSentimentHistory(unfilteredSubmissions);
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredSubmissions, unfilteredSubmissions]);

	// ── FEEDBACK: Average sentiment quartiles ──────────────────

	const averageSentiment = useMemo<ChartSectionData<AverageSentimentData>>(() => {
		const filtered = buildAverageSentiment(filteredSubmissions, myRelevantSubmissions);
		const unfiltered = buildAverageSentiment(unfilteredSubmissions, myRelevantSubmissions);
		return makeSection(filtered, unfiltered);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredSubmissions, unfilteredSubmissions, myRelevantSubmissions]);

	// ── Return ─────────────────────────────────────────────────

	return {
		songsPerformed,
		themeDistribution,
		theologyDistribution,
		emotionDistribution,
		gospelStoryDistribution,
		themeTrend,
		theologyTrend,
		emotionTrend,
		gospelStoryTrend,
		radarByGender,
		radarByGeneration,
		radarByServiceType,
		radarBySkill,
		sentimentHistory,
		averageSentiment,
		topTen,
	};
}
